/**
 * ContractorProfileForm Component
 *
 * Form for contractor profile settings
 * Based on Figma design: node-id=1-1448
 */

'use client';

import { useState } from 'react';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Icon } from '@/src/shared/ui/icons/Icon';
import { Checkbox } from '@/src/shared/ui/checkbox/Checkbox';
import { AvatarUpload } from './AvatarUpload';
import { SaveButton } from './SaveButton';
import { CategorySelector } from './CategorySelector';
import { PortfolioSection } from './PortfolioSection';
import { WorkingHoursSection, type WorkingHoursDay } from './WorkingHoursSection';
import { SocialMediaLinksSection, type SocialMediaLink } from './SocialMediaLinksSection';
import { CertificatesSection, type Certificate } from './CertificatesSection';
import { FormInput } from '../FormInput';
import { LocationPickerButton } from './LocationPickerButton';
import { LocationWarningBanner } from './LocationWarningBanner';
import { AddPhotoModal } from '@/src/features/modals/AddPhotoModal';
import type { SaveStatus } from './hooks/useProfileSave';
import type { ContractorProfile } from '@hummii/shared-types';
import type { Category, PortfolioItem } from '../../api';
import type { CategoryWithSubcategories } from './hooks/useCategoryManagement';

export interface ContractorProfileFormProps {
  profileData?: ContractorProfile | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  latitude: number | null;
  longitude: number | null;
  serviceArea: string;
  showLocation: boolean;
  profileDescription: string;
  hourlyRate: string;
  selectedAvatar: File | null;
  avatarUrl: string;
  validationErrors: Record<string, string>;
  saveStatus: SaveStatus;
  isSaving: boolean;
  // Category props
  selectedCategoryGroups: CategoryWithSubcategories[];
  rootCategories: Category[];
  getSubcategories: (parentId: string) => Category[];
  onAddCategory: () => void;
  onRemoveCategory: (index: number) => void;
  onCategoryChange: (index: number, categoryId: string) => void;
  onSubcategoryToggle: (groupIndex: number, subcategoryId: string) => void;
  onSubcategoryPriceChange: (
    groupIndex: number,
    subcategoryId: string,
    field: 'price' | 'unit',
    value: string
  ) => void;
  // Portfolio props
  portfolioData?: PortfolioItem[];
  onPortfolioDelete: (id: string) => Promise<void>;
  isPortfolioDeleting: boolean;
  // Form handlers
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onLocationChange: (location: {
    address: string;
    city: string;
    province: string;
    postalCode: string;
    latitude: number;
    longitude: number;
  }) => void;
  onLocationSave: (location: {
    address: string;
    city: string;
    province: string;
    postalCode: string;
    latitude: number;
    longitude: number;
  }) => Promise<void>;
  onLocationClear?: () => void;
  onServiceAreaChange: (value: string) => void;
  onShowLocationChange: (value: boolean) => void;
  onProfileDescriptionChange: (value: string) => void;
  onHourlyRateChange: (value: string) => void;
  onAvatarChange: (file: File | null) => void;
  onSave: () => void;
  onClearError: (field: string) => void;
  // Working hours props
  workingHours: WorkingHoursDay[];
  onWorkingHoursChange: (hours: WorkingHoursDay[]) => void;
  // Location warning props
  locationWarning?: string | null;
  onDismissLocationWarning?: () => void;
  // Social media links props
  socialMediaLinks: SocialMediaLink[];
  onSocialMediaLinksChange: (links: SocialMediaLink[]) => void;
  onAddSocialMedia: () => void;
  // Certificates props
  certificates: Certificate[];
  onAddCertificates: (files: FileList) => Promise<void>;
  onDeleteCertificate: (id: string) => void;
  // Portfolio photo add handler
  onPortfolioPhotoAdd?: (photo: { file: File; caption: string }) => Promise<void>;
}

// Service area options
const SERVICE_AREA_OPTIONS = [
  { value: '5', label: '5 km' },
  { value: '10', label: '10 km' },
  { value: '15', label: '15 km' },
  { value: '20', label: '20 km' },
  { value: '25', label: '25 km' },
  { value: '30', label: '30 km' },
  { value: '50', label: '50 km' },
  { value: '100', label: '100 km' },
];

export function ContractorProfileForm({
  profileData,
  firstName,
  lastName,
  email,
  phone,
  address,
  city,
  province,
  postalCode,
  latitude,
  longitude,
  serviceArea,
  showLocation,
  profileDescription,
  hourlyRate,
  selectedAvatar,
  avatarUrl,
  validationErrors,
  saveStatus,
  isSaving,
  selectedCategoryGroups,
  rootCategories,
  getSubcategories,
  onAddCategory,
  onRemoveCategory,
  onCategoryChange,
  onSubcategoryToggle,
  onSubcategoryToggle: onSubcategoryToggleProp,
  onSubcategoryPriceChange,
  portfolioData,
  onPortfolioDelete,
  isPortfolioDeleting,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onPhoneChange,
  onLocationChange,
  onLocationSave,
  onLocationClear,
  onServiceAreaChange,
  onShowLocationChange,
  onProfileDescriptionChange,
  onHourlyRateChange,
  onAvatarChange,
  onSave,
  onClearError,
  workingHours,
  onWorkingHoursChange,
  locationWarning,
  onDismissLocationWarning,
  socialMediaLinks,
  onSocialMediaLinksChange,
  onAddSocialMedia,
  certificates,
  onAddCertificates,
  onDeleteCertificate,
  onPortfolioPhotoAdd,
}: ContractorProfileFormProps) {
  // State for portfolio photo modal
  const [isPortfolioModalOpen, setIsPortfolioModalOpen] = useState(false);

  // Parse service area number from string like "20 km"
  const serviceAreaValue = serviceArea.replace(/[^\d]/g, '') || '20';

  // Handle portfolio photo add
  const handlePortfolioPhotoAdd = async (photo: { file: File; caption: string }) => {
    if (onPortfolioPhotoAdd) {
      await onPortfolioPhotoAdd(photo);
    }
    setIsPortfolioModalOpen(false);
  };

  return (
    <div className="space-y-8">
      {/* Location Warning Banner */}
      {locationWarning && (
        <LocationWarningBanner
          message={locationWarning}
          onDismiss={onDismissLocationWarning}
        />
      )}

      {/* Profile Photo and Name Section */}
      <div className="flex gap-8">
        {/* Avatar */}
        <div className="shrink-0">
          <AvatarUpload
            currentAvatarUrl={avatarUrl}
            selectedAvatar={selectedAvatar}
            onAvatarChange={onAvatarChange}
          />
        </div>

        {/* Name and Contact Fields */}
        <div className="flex-1 space-y-5">
          {/* First Name / Last Name */}
          <div className="grid grid-cols-2 gap-5">
            <FormInput
              name="firstName"
              label="First Name"
              value={firstName}
              onChange={(value) => {
                onFirstNameChange(value);
                onClearError('firstName');
              }}
              error={validationErrors.firstName}
              placeholder="John"
              required
            />
            <FormInput
              name="lastName"
              label="Last Name"
              value={lastName}
              onChange={(value) => {
                onLastNameChange(value);
                onClearError('lastName');
              }}
              error={validationErrors.lastName}
              placeholder="Smith"
              required
            />
          </div>

          {/* Email / Phone Number */}
          <div className="grid grid-cols-2 gap-5">
            <FormInput
              name="email"
              label="Email"
              type="email"
              value={email}
              onChange={onEmailChange}
              error={validationErrors.email}
              disabled
            />
            <FormInput
              name="phone"
              label="Phone Number (visible only to you)"
              type="tel"
              value={phone}
              onChange={(value) => {
                // Allow international phone numbers in E.164 format
                if (value === '' || value === '+' || /^\+[1-9]\d{0,14}$/.test(value)) {
                  onPhoneChange(value);
                  onClearError('phone');
                }
              }}
              error={validationErrors.phone}
              placeholder="+11234567890"
            />
          </div>

          {/* Location / Service Area */}
          <div className="grid grid-cols-2 gap-5">
            {/* Location with clear button */}
            <div>
              <label className="block mb-2">
                <Typography variant="body" weight="medium">
                  Location <span className="text-feedback-error">*</span>
                </Typography>
              </label>
              <div className="relative">
                <LocationPickerButton
                  address={address}
                  city={city}
                  province={province}
                  postalCode={postalCode}
                  latitude={latitude}
                  longitude={longitude}
                  error={validationErrors.location || validationErrors.latitude}
                  onLocationChange={onLocationChange}
                  onLocationSave={onLocationSave}
                  onClearError={() => {
                    onClearError('location');
                    onClearError('latitude');
                    onClearError('longitude');
                    onClearError('city');
                    onClearError('postalCode');
                  }}
                />
              </div>
            </div>

            {/* Service Area Dropdown */}
            <div>
              <label className="block mb-2">
                <Typography variant="body" weight="medium">
                  Service area <span className="text-feedback-error">*</span>
                </Typography>
              </label>
              <div className="relative">
                <select
                  value={serviceAreaValue}
                  onChange={(e) => onServiceAreaChange(`${e.target.value} km`)}
                  className="w-full px-4 py-3 border border-border-primary rounded-xl bg-white
                    text-text-primary appearance-none cursor-pointer
                    focus:outline-none focus:border-accent-primary focus:ring-1 focus:ring-accent-primary
                    transition-colors pr-10"
                >
                  {SERVICE_AREA_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <Icon name="arrow-down" size="sm" color="secondary" />
                </div>
              </div>
              {/* Show location checkbox */}
              <div className="mt-3">
                <Checkbox
                  checked={showLocation}
                  onChange={(e) => onShowLocationChange(e.target.checked)}
                  label="Show my location to others"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Description */}
      <div>
        <Typography variant="body" weight="medium" className="mb-2">
          Profile Description
        </Typography>
        <div className="relative">
          <textarea
            value={profileDescription}
            onChange={(e) => {
              onProfileDescriptionChange(e.target.value.slice(0, 600));
              onClearError('profileDescription');
            }}
            placeholder="Write something important about your services"
            rows={4}
            maxLength={600}
            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-1 bg-surface-sunken resize-none ${
              validationErrors.profileDescription
                ? 'border-feedback-error focus:ring-feedback-error'
                : 'border-border-primary focus:ring-accent-primary focus:border-accent-primary'
            }`}
          />
          <div className="absolute bottom-3 left-4">
            <Typography variant="bodySm" color="secondary">
              {profileDescription.length}/600
            </Typography>
          </div>
        </div>
        {validationErrors.profileDescription && (
          <Typography variant="bodySm" className="text-feedback-error mt-1">
            {validationErrors.profileDescription}
          </Typography>
        )}
      </div>

      {/* Category Section */}
      <div>
        <Typography variant="body" weight="medium" className="mb-2">
          Category <span className="text-feedback-error">*</span>
        </Typography>
        <CategorySelector
          selectedCategoryGroups={selectedCategoryGroups}
          rootCategories={rootCategories}
          getSubcategories={getSubcategories}
          onAddCategory={onAddCategory}
          onRemoveCategory={onRemoveCategory}
          onCategoryChange={onCategoryChange}
          onSubcategoryToggle={onSubcategoryToggle}
          onSubcategoryPriceChange={onSubcategoryPriceChange}
        />
      </div>

      {/* Hourly Rate */}
      <div>
        <Typography variant="body" weight="medium" className="mb-2">
          Hourly rate
        </Typography>
        <div className="relative">
          <input
            type="text"
            value={hourlyRate}
            onChange={(e) => {
              onHourlyRateChange(e.target.value);
              onClearError('hourlyRate');
            }}
            placeholder="Enter your average hourly rate"
            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-1 bg-white pr-20 ${
              validationErrors.hourlyRate
                ? 'border-feedback-error focus:ring-feedback-error'
                : 'border-border-primary focus:ring-accent-primary focus:border-accent-primary'
            }`}
          />
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <Typography variant="bodySm" color="secondary">
              $ / Hour
            </Typography>
          </div>
        </div>
        {validationErrors.hourlyRate && (
          <Typography variant="bodySm" className="text-feedback-error mt-1">
            {validationErrors.hourlyRate}
          </Typography>
        )}
      </div>

      {/* Working Hours Section */}
      <WorkingHoursSection
        workingHours={workingHours}
        onChange={onWorkingHoursChange}
        editable
      />

      {/* Divider */}
      <hr className="border-border-primary" />

      {/* Social Media Links */}
      <SocialMediaLinksSection
        socialMediaLinks={socialMediaLinks}
        onSocialMediaLinksChange={onSocialMediaLinksChange}
        onAddSocialMedia={onAddSocialMedia}
      />

      {/* Divider */}
      <hr className="border-border-primary" />

      {/* Certificates and Diplomas */}
      <CertificatesSection
        certificates={certificates}
        onCertificatesChange={() => {}}
        onAddCertificates={onAddCertificates}
        onDeleteCertificate={onDeleteCertificate}
      />

      {/* Divider */}
      <hr className="border-border-primary" />

      {/* Portfolio */}
      <PortfolioSection
        portfolioData={portfolioData}
        onDelete={onPortfolioDelete}
        isDeleting={isPortfolioDeleting}
        onAddPhoto={() => setIsPortfolioModalOpen(true)}
      />

      {/* Save Button */}
      <div className="flex justify-end pt-6 border-t border-border-primary">
        <SaveButton onClick={onSave} isLoading={isSaving} status={saveStatus} />
      </div>

      {/* Portfolio Photo Modal */}
      <AddPhotoModal
        isOpen={isPortfolioModalOpen}
        onClose={() => setIsPortfolioModalOpen(false)}
        singlePhotoMode
        title="Add photo"
        onPhotoWithCaptionAdded={handlePortfolioPhotoAdd}
      />
    </div>
  );
}
